import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'
import {
  useLazySvgTextSize,
  useSvgBbox,
  useTranslate,
} from '@festina-life/ui/hooks'
import { Group } from '@visx/group'
import { Fragment, type MouseEvent, useEffect, useMemo, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateId } from '../../hooks/useTemplateId'
import { useTemplateNavigation } from '../../system/useTemplateNavigation'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { useTemplateHierarchy } from '../../uiModels/versionInfo/useTemplateHierarchy'
import { useTemplateVersionInfo } from '../../uiModels/versionInfo/useTemplateVersionInfo'
import { DATE_HEADING_HEIGHT } from '../versionedTimeline/constants'
import {
  NODE_COLLAPSED_SPACING_VERTICAL,
  REPLACEMENT_LINE_PADDING,
  STROKE_WIDTH,
  TEXT_BOX_PADDING_X,
  TEXT_BOX_PADDING_Y,
} from './constants'
import { InheritancePathVertical } from './InheritancePathVertical'
import { InheritanceTooltip } from './InheritanceTooltip'
import { InheritanceTreeNode } from './InheritanceTreeNode'
import { ReplacementPath } from './ReplacementPath'
import { useReplacedTemplatesOutsideHierarchy } from './useReplacedTemplatesOutsideHierarchy'

type Props = {
  onNodeHeightChanged?: (height: number) => void
  onViewMore?: () => void
}

export function InheritanceDiagram({ onNodeHeightChanged, onViewMore }: Props) {
  const navigate = useTemplateNavigation()
  const translate = useTranslate()
  const templateId = useTemplateId()
  const { viewAt } = useConfigurationContext()
  const versionInfo = useTemplateVersionInfo({
    id: templateId,
    date: viewAt,
  })
  const {
    preferences: {
      templates: { isFlattened },
    },
  } = useLayoutContext()
  const [hoveredVersion, setHoveredVersion] =
    useState<Optional<TemplateVersionInfo>>()

  assertNotEmpty(versionInfo)
  const {
    ref,
    width: bboxWidth,
    height: bboxHeight,
  } = useSvgBbox({ x: 0, y: STROKE_WIDTH + DATE_HEADING_HEIGHT })

  const { dimensions, svgRef: svgRef, didMount } = useLazySvgTextSize()
  const hierarchy = useTemplateHierarchy({
    forTemplate: versionInfo,
    withReplacements: false,
  })
  const isPlottingReplacements = hierarchy.some(
    (version) => !!version.replacesId,
  )
  const replacedVersions = useReplacedTemplatesOutsideHierarchy(hierarchy)

  const trailingTemplate = useMemo(() => {
    const children = hierarchy[hierarchy.length - 1]?.children

    if (!children?.length) {
      return null
    } else if (children.length === 1) {
      return {
        text: children[0].identifier,
        template: children[0],
        replaces: children[0].replaces,
        isMissing: children[0].isMissing,
      }
    } else {
      return {
        text: translate('templateViewer.sidebar.groupedNode', {
          count: children.length,
        }),
      }
    }
  }, [hierarchy, translate])

  const { stringToDimensions, maxHeight, maxWidth } = useMemo(() => {
    if (!didMount) {
      return { stringToDimensions: {}, maxWidth: 0, maxHeight: 0 }
    }

    // Find the widest string to right-align the SVG elements.
    // The tallest height is propagated to align version arrows.
    const strings = hierarchy.map(
      (templateVersion) => templateVersion.identifier,
    )
    replacedVersions.forEach((replacedVersion) => {
      strings.push(replacedVersion.identifier)
    })

    if (trailingTemplate?.replaces) {
      strings.push(trailingTemplate.replaces.identifier)
    }

    if (versionInfo.children.length === 1) {
      // Will render the child's identifier directly.
      strings.push(versionInfo.children[0].identifier)
    } else if (versionInfo.children.length) {
      // Will render a non-interactive "group" node.
      strings.push(
        translate('templateViewer.sidebar.groupedNode', {
          count: hierarchy[hierarchy.length - 1].children.length,
        }),
      )
    }

    const uniqueStrings = Array.from(new Set(strings))
    const measured = dimensions(uniqueStrings)
    const stringToDimensions = Object.fromEntries(
      uniqueStrings.map((string, index) => [string, measured[index]]),
    )
    const maxWidth = Math.max(
      ...Object.values(measured).map(({ width }) => width),
    )
    const maxHeight = Math.max(
      ...Object.values(measured).map(
        ({ height }) => height + 2 * TEXT_BOX_PADDING_Y,
      ),
    )

    return { stringToDimensions, maxWidth, maxHeight }
  }, [
    didMount,
    dimensions,
    hierarchy,
    replacedVersions,
    trailingTemplate?.replaces,
    translate,
    versionInfo.children,
  ])

  useEffect(() => {
    onNodeHeightChanged?.(maxHeight)
  }, [maxHeight, onNodeHeightChanged])

  const handleMouseOver = (
    _event: MouseEvent<SVGRectElement>,
    version: TemplateVersionInfo,
  ) => {
    setHoveredVersion(version)
  }

  const leftReplacementsSpacing = isPlottingReplacements
    ? REPLACEMENT_LINE_PADDING
    : 0

  const nodeXPosition = (identifier: string) =>
    leftReplacementsSpacing +
    (maxWidth - (stringToDimensions[identifier]?.width ?? 0)) / 2
  const nodeYPosition = (position: number, offset = 0) =>
    (position + offset) * (maxHeight + NODE_COLLAPSED_SPACING_VERTICAL)

  return (
    <svg
      width={bboxWidth + leftReplacementsSpacing}
      height={bboxHeight}
      ref={ref}
    >
      <Group top={DATE_HEADING_HEIGHT} innerRef={svgRef}>
        {replacedVersions.map((replacedVersion, i) => (
          <Tooltip
            key={replacedVersion.identifier}
            tip={<InheritanceTooltip versionInfo={replacedVersion} />}
          >
            <g>
              <InheritanceTreeNode
                x={nodeXPosition(replacedVersion.identifier)}
                y={i * (maxHeight + NODE_COLLAPSED_SPACING_VERTICAL)}
                text={replacedVersion.identifier}
                onClick={() =>
                  navigate({
                    templateId: replacedVersion.identifier,
                    layer: replacedVersion.layer,
                    block: replacedVersion.block,
                  })
                }
                onMouseOver={(e) => handleMouseOver(e, replacedVersion)}
                onMouseOut={() => setHoveredVersion(undefined)}
                isMissing={replacedVersion.isMissing}
              />
            </g>
          </Tooltip>
        ))}
        {/* Not using a Tree because we have a list and we need specific positioning anyway */}
        {hierarchy.map((templateVersion, i) => (
          <Fragment key={templateVersion.identifier}>
            {i !== 0 && (
              <InheritancePathVertical
                x={maxWidth / 2 + TEXT_BOX_PADDING_X + leftReplacementsSpacing}
                y={
                  (i + replacedVersions.length) *
                  (maxHeight + NODE_COLLAPSED_SPACING_VERTICAL)
                }
              />
            )}
            {templateVersion.replacesId && (
              <ReplacementPath
                isActive={
                  hoveredVersion?.identifier === templateVersion.identifier ||
                  hoveredVersion?.identifier === templateVersion.replacesId
                }
                from={{
                  x: nodeXPosition(templateVersion.identifier),
                  y: nodeYPosition(i, replacedVersions.length) + maxHeight / 2,
                }}
                to={{
                  x: nodeXPosition(templateVersion.replacesId),
                  y:
                    nodeYPosition(
                      [...replacedVersions, ...hierarchy].findIndex(
                        (version: TemplateVersionInfo) =>
                          version.identifier === templateVersion.replacesId,
                      ),
                    ) +
                    maxHeight / 2,
                }}
              />
            )}
            <Tooltip tip={<InheritanceTooltip versionInfo={templateVersion} />}>
              <g>
                <InheritanceTreeNode
                  x={nodeXPosition(templateVersion.identifier)}
                  y={nodeYPosition(i, replacedVersions.length)}
                  text={templateVersion.identifier}
                  isActive={
                    isFlattened || templateVersion.identifier === templateId
                  }
                  isCurrent={templateVersion.identifier === templateId}
                  onClick={() =>
                    navigate({
                      templateId: templateVersion.identifier,
                      layer: templateVersion.layer,
                      block: templateVersion.block,
                    })
                  }
                  onMouseOver={(e) => handleMouseOver(e, templateVersion)}
                  onMouseOut={() => setHoveredVersion(undefined)}
                  isMissing={templateVersion.isMissing}
                />
              </g>
            </Tooltip>
          </Fragment>
        ))}
        {trailingTemplate && (
          <>
            <InheritancePathVertical
              x={maxWidth / 2 + TEXT_BOX_PADDING_X + leftReplacementsSpacing}
              y={nodeYPosition(hierarchy.length + replacedVersions.length)}
            />
            <InheritanceTreeNode
              x={nodeXPosition(trailingTemplate.text)}
              y={nodeYPosition(hierarchy.length + replacedVersions.length)}
              text={trailingTemplate.text}
              onClick={
                trailingTemplate.template
                  ? () =>
                      navigate({
                        templateId: trailingTemplate.template.identifier,
                        layer: trailingTemplate.template.layer,
                        block: trailingTemplate.template.block,
                      })
                  : onViewMore
              }
              onMouseOver={(e) => {
                if (trailingTemplate.template) {
                  handleMouseOver(e, trailingTemplate.template)
                }
              }}
              onMouseOut={() => setHoveredVersion(undefined)}
              isMissing={trailingTemplate.isMissing}
            />
            {trailingTemplate.replaces && (
              <ReplacementPath
                isActive={
                  hoveredVersion?.identifier ===
                    trailingTemplate?.template.identifier ||
                  hoveredVersion?.identifier ===
                    trailingTemplate?.replaces.identifier
                }
                from={{
                  x: nodeXPosition(trailingTemplate.text),
                  y:
                    nodeYPosition(hierarchy.length + replacedVersions.length) +
                    maxHeight / 2,
                }}
                to={{
                  x: nodeXPosition(trailingTemplate.replaces.identifier),
                  y:
                    nodeYPosition(
                      [...replacedVersions, ...hierarchy].findIndex(
                        (version: TemplateVersionInfo) =>
                          version.identifier ===
                          trailingTemplate.replaces?.identifier,
                      ),
                    ) +
                    maxHeight / 2,
                }}
              />
            )}
          </>
        )}
      </Group>
    </svg>
  )
}
